// Smart Sash Admin Panel - User Permission Management Page

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from '@/components/common';
import { PermissionHeader, PermissionMatrix } from '@/components/permission';
import { useUserPermissionManagement } from '@/hooks/useUserPermissionManagement';
import { useDirtyStateWarning } from '@/hooks';

export const UserPermissionManagement: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    isLoading,
    isSaving,
    error,
    isDirty,
    rolesWithPermissions,
    allPermissionRows,
    selectedPermissionIds,
    togglePermission,
    selectAllForRole,
    replaceWithRole,
    setFullAccess,
    handleSave,
    clearError,
  } = useUserPermissionManagement();

  useDirtyStateWarning(isDirty);

  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User'
    : 'Loading...';

  if (isLoading) {
    return (
      <div className="container-fluid py-4 text-center">
        <Spinner />
        <p className="text-muted mt-2">Loading user permissions...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <PermissionHeader
        userName={userName}
        isDirty={isDirty}
        isSaving={isSaving}
        onBack={() => navigate('/users')}
        onSave={handleSave}
      />

      {error && (
        <div className="row mb-3">
          <div className="col-6">
            <Alert variant="danger" dismissible onDismiss={clearError}>
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
            </Alert>
          </div>
        </div>
      )}

      {isDirty && (
        <div className="row mb-3">
          <div className="col-6">
            <Alert variant="warning">
              <i className="fas fa-exclamation-triangle me-2"></i>
              You have unsaved changes.
            </Alert>
          </div>
        </div>
      )}

      <PermissionMatrix
        roles={rolesWithPermissions}
        allPermissionRows={allPermissionRows}
        selectedPermissionIds={selectedPermissionIds}
        onToggle={togglePermission}
        onSelectAllForRole={selectAllForRole}
        onReplaceWithRole={replaceWithRole}
        onSetFullAccess={setFullAccess}
      />
    </div>
  );
};

export default UserPermissionManagement;
